// A piece's picture: the kit's icon where there is one, else its glyph.
//
// The same shape as `Hotbar`'s `StructureMark` and `hand.ts`'s `cardGlyph`,
// for the same reason — the registry does not draw everything, and a name it
// does not hold is a crash rather than a fallback. **The six ranks are the
// kit's `PieceIcon`s by name**; the monster is the one rank drawn as text, and
// its glyph arrives on the view (`ForgeTile.glyph`) rather than from a table
// here, since the client owns no glyph tables.

import type { PieceIcon, PieceName } from "src/api";
import { PIECE_TYPE } from "src/client/ai/render/factNames";
import type { PieceType } from "src/shared/board";
import { Icon } from "./kit";

/** The ranks the kit draws, by name. The monster is not one of them. */
const ICONS: readonly PieceIcon[] = ["pawn", "knight", "bishop", "rook", "queen", "king"];

/** Name for the rules' number — the render seam, read backwards. */
const NAME_OF = Object.fromEntries(
  Object.entries(PIECE_TYPE).map(([name, type]) => [type, name as PieceName])
) as Record<PieceType, PieceName>;

export type PieceMarkProps = {
  /** Square, in px. The glyph fallback takes the surrounding font size. */
  size?: number;
  /**
   * Sat in a line of text rather than centred in a tile: the icon drops to
   * `inline-block` on the baseline so "♟ Pawn" stays one line.
   */
  inline?: boolean;
  /** What to draw where the kit has no figure — the view's `glyph`. */
  glyph?: string;
} & (
  | { piece: PieceName; type?: undefined }
  /** The rules' number, for a caller still keyed on it. */
  | { type: PieceType; piece?: undefined }
);

export function PieceMark({ type, piece, glyph, size, inline }: PieceMarkProps) {
  const name = piece ?? NAME_OF[type as PieceType];
  const icon = ICONS.find((one) => one === name);
  // No figure, no glyph given: the monster's own mark, which is what the
  // engine's table says too — one place would be better, and that place is
  // the view.
  if (!icon) return <>{glyph ?? "✦"}</>;
  const svg = <Icon name={icon} size={size} />;
  return inline ? (
    <span style={{ display: "inline-block", verticalAlign: "-0.2em" }}>{svg}</span>
  ) : (
    svg
  );
}
